/**
 * BESTANDTEILE EINER PAKETPOSITION — Dialog und Liste fuer Mitarbeiter
 * (Plan docs/positionsbestandteile-plan.md, Stufe B3; Paket D).
 *
 * `BestandteileListe` ist der Inhalt (Stand, Status der Position, je
 * Bestandteil eine Zeile mit Nachweis und Knoepfen, Verlauf, fuer Admins der
 * Editor). `BestandteileDialog` legt sie in ein Modal — der Hub oeffnet es aus
 * dem Schnellzugriff, `ServiceDetailView` bettet die Liste direkt ein.
 *
 * Grundsaetze, die hier sichtbar werden:
 *  - Wer und wann setzt die DATENBANK (Kernregel 10): der Dialog schickt nur
 *    Key, Leistungstag, Notiz bzw. Grund; Zeitpunkt und Person kommen aus den
 *    RPCs zurueck und stehen dann in der Zeile.
 *  - Die Position wird NUR an der ausloesenden Handlung gebucht (Kernregel 7):
 *    nach dem Klick, der den letzten Bestandteil erledigt
 *    (nachHandErledigung), nach „Entfällt", nach dem Speichern einer Liste, die
 *    dadurch vollstaendig wird, und auf den bewussten Knopf „Position jetzt
 *    buchen". NIE beim Oeffnen oder Neuladen der Ansicht.
 *  - Keine Preise (Kernregel 6), keine Browser-Dialoge: Rueckfragen stehen
 *    inline in der Zeile.
 *  - Nur-Ansicht (schreibgeschuetzt, Freelancer, Tochterprojekt) zeigt alles,
 *    bietet aber keinen Knopf an; der Server prueft ohnehin.
 *
 * Die Regeln (welcher Knopf, welcher Text, welche Meldung) stehen in
 * bestandteileDialogLogic.ts und sind dort getestet.
 *
 * STUNDENKONTINGENT (docs/stundenkontingent-vertrag.md, Paket D): der Kopf
 * zeigt die erfassten Stunden gegen das Kontingent (gruen/rot, S5), Admins
 * legen das Kontingent dort fest. Erledigen und „Übernehmen" verlangen Stunden
 * (S2), „Entfällt" nimmt sie freiwillig; je Zeile lassen sich fehlende
 * nachtragen und eingetragene mit Grund aendern (S7). Rein intern (S8) — kennt
 * die Datenbank die Stunden noch nicht (Nr. 171/172 fehlen), entfaellt der
 * ganze Stundenbereich und es wird ohne Stunden gebucht.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    AlertTriangle,
    CheckCircle2,
    ChevronDown,
    ChevronUp,
    Circle,
    Clock,
    ExternalLink,
    History,
    ListChecks,
    Loader2,
    MinusCircle,
    Pencil,
    RotateCcw,
    X,
} from 'lucide-react';
import type { BestandteilQuelle, PositionsBestandteil, Report, ServiceItem, ServiceTransaction } from '../../types';
import {
    BESTANDTEIL_QUELLE_LABEL,
    bestandteilStand,
    hatBestandteile,
    effektivesAb,
    formatZeitpunkt,
    heuteBerlin,
    istModulQuelle,
    normalisiereBestandteile,
    uebernahmeVorschlag,
    verlauf,
    type AusgabeLike,
    type BestandteilAenderung,
    type BestandteilEintrag,
    type BestandteilZeile,
} from '../../services/bestandteile/bestandteilLogic';
import {
    bestandteilEntfaellt,
    erledigeBestandteil,
    ladeAusgabenFuerUebernahme,
    ladeBestandteilEintraege,
    ladePaketPositionenFrisch,
    nimmBestandteilZurueck,
    setzeBestandteilStunden,
    setzeStundenKontingent,
} from '../../services/bestandteile/bestandteilService';
import { stundenStand, type StundenProtokollEintrag } from '../../services/bestandteile/stundenLogic';
import {
    bucheVollstaendigePosition,
    leistungAusEintraegen,
    nachBestandteilRuecknahme,
    nachHandErledigung,
    type BuchungsKontext,
} from '../../services/bestandteile/bestandteilBuchung';
import { getServiceLog } from '../../services/serviceJournal';
import { supabase } from '../../services/supabaseClient';
import { describeError } from '../../services/errorMessage';
import { bestandteilStandKurz } from '../../services/projectLv';
import { BestandteileEditor } from './BestandteileEditor';
import {
    AUSLOESER_KNOPF,
    belegteKeys,
    bewerteFrischenStand,
    dbOffenAusFrisch,
    definitionUnlesbar,
    dialogRechte,
    REPARATUR_MELDUNG,
    UNLESBAR_HINWEIS,
    JETZT_BUCHEN_NUR_ADMIN,
    jetztBuchenFreigabe,
    meldungProjektGewechselt,
    projektIdVon,
    schuetzeSchreibweg,
    type Schreibweg,
    editorWarnungen,
    entfaelltText,
    erledigtText,
    findeKatalogVorlage,
    grundGueltig,
    herkunftText,
    istTochterBericht,
    keysMitAktivemEintrag,
    leistungstagGueltig,
    leistungstagText,
    meldungNachDefinition,
    meldungNachErledigung,
    meldungNachJetztBuchen,
    meldungNachRuecknahme,
    planeDefinitionSpeichern,
    positionsStatus,
    rpcMeldung,
    standText,
    uebernahmeInfo,
    uebernahmeText,
    zeilenKnoepfe,
    berichtMitStunden,
    entfaelltStunden,
    erledigenStunden,
    gleicheStundenFrisch,
    KONTINGENT_FORMAT_HINWEIS,
    kontingentAusEingabe,
    kontingentFelderAusAntwort,
    kontingentFuerKopf,
    meldungNachKontingent,
    meldungNachStunden,
    STUNDEN_FORMAT_HINWEIS,
    STUNDEN_FORMAT_HINWEIS_ENTFAELLT,
    STUNDEN_NICHT_VERFUEGBAR,
    stundenAusFrisch,
    stundenInzwischenEingetragen,
    stundenKopf,
    stundenSetzenEingabe,
    stundenVorbelegung,
    zeilenStunden,
    type Meldung,
    type StundenFrisch,
    type StundenTon,
} from './bestandteileDialogLogic';

/**
 * Was Dialog und Liste vom Projekt brauchen — der Hub baut es einmal und
 * reicht es an Schnellzugriff, Details und Dialog durch.
 */
export interface BestandteileKontext {
    /** Projekt, dem die Position gehoert (Eintraege und RPCs). */
    projectId: string;
    report: Report;
    onUpdate: (r: Report) => void;
    /** Der JETZT geltende Stand (Ref im Hub) — zwischen Klick und Buchung liegt eine Netzrunde. */
    holeBericht: () => Report;
    user: { id: string | null; name: string };
    isAdmin: boolean;
    isReadOnly: boolean;
    istFreelancer?: boolean;
    /** Protokoll fuer Status und Verlauf (Tochter: das der Mutter). Ohne: aus `report`. */
    serviceLog?: readonly ServiceTransaction[];
    /**
     * Ist der Hub noch eingehaengt? Ein Vorgang, der danach fertig wird,
     * schreibt nichts mehr (schuetzeSchreibweg). Ohne Angabe: ja.
     */
    istEingehaengt?: () => boolean;
    /** „Zum Modul" — ohne Angabe kein Link. */
    onNavigateModule?: (quelle: BestandteilQuelle) => void;
    /**
     * Meldet den frischen Stand „offen" (RPC-Antwort bzw. erstes Laden) — fuer
     * die Plakette im Hub, ohne dafuer zu speichern. `offen` undefined: dazu
     * nichts Frisches (der bisherige Wert bleibt). `stunden` (optional): die
     * frischen Stunden der Position laut Datenbank — nur, was sie kennt;
     * erledigen und „Entfällt" melden die neue Summe sonst nicht zurueck.
     */
    onOffenGeaendert?: (service: ServiceItem, offen: number | null | undefined, unlesbar: boolean, stunden?: StundenFrisch) => void;
}

export interface BestandteileListeProps extends BestandteileKontext {
    service: ServiceItem;
    /** Editor gleich offen (ServiceDetailView: „Bestandteile festlegen"). */
    editorStartOffen?: boolean;
}

type Daten =
    | { art: 'laedt' }
    | { art: 'fehler'; text: string }
    | {
        art: 'ok';
        eintraege: BestandteilEintrag[];
        aenderungen: BestandteilAenderung[];
        /** Stunden- und Kontingentaenderungen (Nr. 171) — fuer den Verlauf. */
        stundenProtokoll: StundenProtokollEintrag[];
        /** false: die Datenbank kennt die Stunden nicht — Stundenbereich aus, Buchen ohne Stunden. */
        stundenVerfuegbar: boolean;
        ausgaben: AusgabeLike[];
        ausgabenFehler: string | null;
    };

type Aktion =
    /** Mit `ausgabe`: „Übernehmen" — dasselbe Formular, ohne Leistungstag (S2: Stunden Pflicht). */
    | { art: 'erledigen'; key: string; ausgabe?: AusgabeLike | null }
    | { art: 'entfaellt'; key: string }
    | { art: 'zuruecknehmen'; key: string; eintragId: string }
    /** Stunden nachtragen (`aendern` false) oder aendern (mit Grund, S7). */
    | { art: 'stunden'; key: string; eintragId: string; aendern: boolean }
    | { art: 'jetzt_buchen' }
    /** Kontingent festlegen/aendern/entfernen (nur Admin). */
    | { art: 'kontingent' }
    | null;

type Ergebnis = { meldung: Meldung; schliessen: boolean };

const KEINE_EINTRAEGE: BestandteilEintrag[] = [];
const KEINE_AENDERUNGEN: BestandteilAenderung[] = [];
const KEINE_AUSGABEN: AusgabeLike[] = [];
const KEIN_PROTOKOLL: StundenProtokollEintrag[] = [];

const STUNDEN_TEXT: Record<StundenTon, string> = {
    gruen: 'text-emerald-800',
    rot: 'text-red-700',
    grau: 'text-slate-600',
};
const STUNDEN_BALKEN: Record<StundenTon, string> = {
    gruen: 'bg-emerald-500',
    rot: 'bg-red-500',
    grau: 'bg-slate-400',
};

const ANLASS_LABEL: Record<string, string> = { freigabe: 'Freigabe', versand: 'Versand' };

const knopf = 'inline-flex min-h-11 items-center justify-center gap-1.5 rounded-lg px-3 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-9';
const knopfPrimaer = `${knopf} bg-blue-600 text-white hover:bg-blue-700`;
const knopfSekundaer = `${knopf} border border-slate-200 bg-white text-slate-700 hover:bg-slate-50`;
const knopfLeise = `${knopf} text-slate-600 hover:bg-slate-100`;
const feld = 'mt-1 w-full min-h-11 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 sm:min-h-9';
const feldLabel = 'block text-[11px] font-semibold uppercase tracking-wide text-slate-500';

const MELDUNG_KLASSE: Record<Meldung['ton'], string> = {
    erfolg: 'border-emerald-200 bg-emerald-50 text-emerald-900',
    hinweis: 'border-amber-200 bg-amber-50 text-amber-900',
    fehler: 'border-red-200 bg-red-50 text-red-900',
};

const STATUS_CHIP: Record<BestandteilZeile['status'], { text: string; klasse: string }> = {
    offen: { text: 'offen', klasse: 'border-slate-200 bg-slate-100 text-slate-600' },
    erledigt: { text: 'erledigt', klasse: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
    entfaellt: { text: 'entfällt', klasse: 'border-amber-200 bg-amber-50 text-amber-800' },
};

const POSITIONS_CHIP: Record<string, { text: string; klasse: string }> = {
    offen: { text: 'nicht abrechenbar', klasse: 'border-amber-200 bg-amber-50 text-amber-800' },
    gebucht_automatisch: { text: 'gebucht', klasse: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
    gebucht: { text: 'gebucht', klasse: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
    nicht_gebucht: { text: 'nicht gebucht', klasse: 'border-amber-200 bg-amber-50 text-amber-800' },
    unlesbar: { text: 'gesperrt', klasse: 'border-slate-200 bg-slate-100 text-slate-600' },
};

/** Die Katalogvorlage (gleiche LV-Nummer UND gleicher Titel). Fehler → keine Vorlage. */
const ladeKatalogVorlage = async (service: Pick<ServiceItem, 'lvPosition' | 'title'>): Promise<PositionsBestandteil[] | null> => {
    const lv = String(service?.lvPosition ?? '').trim();
    if (!lv) return null;
    try {
        const { data, error } = await supabase.from('service_catalog').select('title, lv_position, bestandteile').eq('lv_position', lv);
        if (error) return null;
        return findeKatalogVorlage((data || []) as Record<string, unknown>[], service);
    } catch {
        return null;
    }
};

const gleicheId = (a: unknown, b: unknown): boolean =>
    String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase();

export const BestandteileListe: React.FC<BestandteileListeProps> = ({
    projectId,
    report,
    onUpdate,
    holeBericht,
    user,
    isAdmin,
    isReadOnly,
    istFreelancer = false,
    serviceLog,
    onNavigateModule,
    onOffenGeaendert,
    istEingehaengt,
    service,
    editorStartOffen = false,
}) => {
    const [daten, setDaten] = useState<Daten>({ art: 'laedt' });
    const [ladeZaehler, setLadeZaehler] = useState(0);
    const [aktion, setAktion] = useState<Aktion>(null);
    const [leistungstag, setLeistungstag] = useState(() => heuteBerlin());
    const [notiz, setNotiz] = useState('');
    const [grund, setGrund] = useState('');
    // Stunden-Eingabe des offenen Formulars (erledigen, entfaellt, nachtragen/
    // aendern, Kontingent) — als Text, Komma oder Punkt (parseStunden).
    const [stundenEingabe, setStundenEingabe] = useState('');
    const [laeuft, setLaeuft] = useState(false);
    const [meldung, setMeldung] = useState<Meldung | null>(null);
    const [verlaufOffen, setVerlaufOffen] = useState(false);
    const [editorOffen, setEditorOffen] = useState(editorStartOffen);
    const [entwurf, setEntwurf] = useState<PositionsBestandteil[] | null>(() => normalisiereBestandteile(service.bestandteile));
    const [vorlage, setVorlage] = useState<PositionsBestandteil[] | null>(null);
    const [editorFehler, setEditorFehler] = useState<string | null>(null);
    // Offene Bestandteile laut DATENBANK — aus DERSELBEN Ladung wie die
    // Eintraege (dbOffenAusFrisch). undefined: kein frischer Wert zu dieser
    // Liste bekannt — dann gibt es keinen Befund „unlesbar".
    const [dbOffen, setDbOffen] = useState<number | null | undefined>(undefined);
    // Stunden der Position laut DATENBANK aus derselben Ladung (Kontingent,
    // Summe) — undefined: nichts Frisches bekannt. Nur bei Aenderung neu
    // gesetzt, damit eine gleiche Ladung den Hub nicht erneut meldet.
    const [dbStunden, setDbStundenRoh] = useState<StundenFrisch | undefined>(undefined);
    const setDbStunden = (neu: StundenFrisch | undefined) =>
        setDbStundenRoh((vorher) => (gleicheStundenFrisch(vorher, neu) ? vorher : neu));

    // Immer die neueste Position und der neueste Rueckruf, ohne das Laden neu
    // anzustossen (Muster HubOverlay).
    const aktuell = useRef({ service, onOffenGeaendert });
    useEffect(() => {
        aktuell.current = { service, onOffenGeaendert };
    });

    // ── Laden: Eintraege + Aenderungen, Ausgaben (Uebernahme), frischer Stand ──
    // Gebucht wird hier NIE (Kernregel 7) — das Laden zeigt nur an. Bei JEDER
    // Ladung (Oeffnen und nach jeder Handlung, auch nach konkurrierenden
    // Ergebnissen wie schon_erledigt/veraltet) kommen Eintraege UND
    // Positionsstand zusammen frisch; erst daraus wird „unlesbar" bewertet
    // (bewerteFrischenStand). Zeigt die Ladung den Befund, folgt eine
    // Bestaetigungsrunde: ein Kollege kann zwischen den beiden Abfragen
    // gebucht haben.
    useEffect(() => {
        let abgebrochen = false;
        const lade = async () => {
            const s = aktuell.current.service;
            const frischVon = (pos: Awaited<ReturnType<typeof ladePaketPositionenFrisch>>) =>
                pos.ok ? pos.positionen.find((p) => gleicheId(p.id, s.id)) ?? null : undefined;
            const [ein1, aus, pos1] = await Promise.all([
                ladeBestandteilEintraege(projectId),
                ladeAusgabenFuerUebernahme(projectId),
                ladePaketPositionenFrisch(projectId),
            ]);
            let ein = ein1;
            let pos = pos1;
            if (ein.ok && bewerteFrischenStand({ service: s, eintraege: ein.eintraege, frisch: frischVon(pos) }).unlesbar) {
                const [ein2, pos2] = await Promise.all([ladeBestandteilEintraege(projectId), ladePaketPositionenFrisch(projectId)]);
                ein = ein2;
                pos = pos2;
            }
            if (abgebrochen) return;
            if (ein.ok === false) {
                setDaten({ art: 'fehler', text: ein.fehler });
                setDbOffen(undefined);
                setDbStunden(undefined);
                return;
            }
            setDaten({
                art: 'ok',
                eintraege: ein.eintraege,
                aenderungen: ein.aenderungen,
                // Aeltere Dienst-Antworten (Testdoppel, Rueckfall) ohne die
                // Felder: kein Protokoll, Stunden wie bisher verfuegbar.
                stundenProtokoll: ein.stundenProtokoll ?? [],
                stundenVerfuegbar: ein.stundenVerfuegbar !== false,
                ausgaben: aus.ok ? aus.ausgaben : [],
                ausgabenFehler: aus.ok === false ? aus.fehler : null,
            });
            const frisch = frischVon(pos);
            setDbOffen(dbOffenAusFrisch(s, frisch));
            setDbStunden(stundenAusFrisch(s, frisch));
        };
        void lade().catch((e) => {
            if (!abgebrochen) setDaten({ art: 'fehler', text: describeError(e) });
        });
        return () => {
            abgebrochen = true;
        };
    }, [projectId, ladeZaehler]);

    // Katalogvorlage erst, wenn der Editor offen ist.
    const lvPosition = service.lvPosition;
    const titel = service.title;
    useEffect(() => {
        if (!editorOffen) return;
        let abgebrochen = false;
        void ladeKatalogVorlage({ lvPosition, title: titel }).then((v) => {
            if (!abgebrochen) setVorlage(v);
        });
        return () => {
            abgebrochen = true;
        };
    }, [editorOffen, lvPosition, titel]);

    // Die Meldung steht oben; nach einem Klick weit unten (Handy) holt sie
    // sich ins Bild, sonst ginge „Position gebucht" unbemerkt vorbei.
    const meldungRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const el = meldungRef.current;
        if (meldung && el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, [meldung]);

    const eintraege = daten.art === 'ok' ? daten.eintraege : KEINE_EINTRAEGE;
    const aenderungen = daten.art === 'ok' ? daten.aenderungen : KEINE_AENDERUNGEN;
    const ausgaben = daten.art === 'ok' ? daten.ausgaben : KEINE_AUSGABEN;
    const log = useMemo(() => serviceLog ?? getServiceLog(report), [serviceLog, report]);
    const stand = useMemo(() => bestandteilStand(service, eintraege), [service, eintraege]);
    const geladen = daten.art === 'ok';
    // Zaehlt die Datenbank mehr offene als lesbar? Dann gesperrt, Hinweis, kein
    // Buchen. NUR gegen den frischen DB-Wert derselben Ladung — nie gegen den
    // Wert im geladenen Projekt (nach der Handlung eines Kollegen veraltet).
    const dbOffenFrisch = geladen && dbOffen !== undefined ? dbOffen : null;
    const unlesbar = geladen && dbOffen !== undefined && definitionUnlesbar(stand, dbOffen);
    const status = useMemo(
        () => positionsStatus({ service, stand, serviceLog: log, dbOffen: dbOffenFrisch }),
        [service, stand, log, dbOffenFrisch],
    );

    // Den frischen DB-Wert (und den Befund „unlesbar") an den Hub melden — fuer
    // die Plakette, ohne dafuer zu speichern. Nur Bekanntes: undefined (nichts
    // Frisches) meldet nichts, null (unbekannt) loescht die Ueberschreibung.
    // Die frischen Stunden gehen mit (Stunden-Plakette): erledigen/entfaellt
    // melden die neue Summe nicht zurueck, erst diese Ladung kennt sie.
    useEffect(() => {
        if (dbOffen === undefined && dbStunden === undefined) return;
        const { service: s, onOffenGeaendert: melde } = aktuell.current;
        melde?.(s, dbOffen, unlesbar, dbStunden);
    }, [dbOffen, unlesbar, dbStunden]);
    const stundenProtokoll = daten.art === 'ok' ? daten.stundenProtokoll : KEIN_PROTOKOLL;
    const verlaufListe = useMemo(
        () => verlauf({ service, eintraege, aenderungen, serviceLog: log, stundenProtokoll }),
        [service, eintraege, aenderungen, log, stundenProtokoll],
    );
    // ── Stunden (rein intern, S8) ──────────────────────────────────────────
    // Nur bei einem Paket und nur, wenn die Datenbank die Stunden kennt.
    const stundenVerfuegbar = daten.art === 'ok' && daten.stundenVerfuegbar;
    const stundenAn = stundenVerfuegbar && hatBestandteile(service);
    const kontingent = kontingentFuerKopf(service, dbStunden);
    const kopfStunden = useMemo(
        () => stundenKopf(stundenStand({ id: service.id, bestandteile: service.bestandteile, stundenKontingent: kontingent }, eintraege)),
        [service.id, service.bestandteile, kontingent, eintraege],
    );
    const rechte = dialogRechte({
        isReadOnly,
        istFreelancer,
        istTochter: istTochterBericht(report),
        ohneProjekt: !projectId || projectId === 'temp-id',
    });
    // Knoepfe nur mit geladenem Stand — ohne Eintraege waere „offen" geraten.
    const aktiv = rechte.schreibbar && geladen;
    // Kontingent festlegen/aendern/entfernen: nur Admin, nur am Paket (S1).
    const darfKontingent = isAdmin && aktiv && stundenAn;
    const heute = heuteBerlin();

    // Jeder Vorgang wird beim START an sein Projekt gebunden (Stand des Klicks).
    // Zwischen Klick und Positionsbuchung liegen Netzrunden; wechselt in der
    // Zeit das Projekt oder verschwindet der Hub, schreibt der Vorgang nichts
    // mehr (schuetzeSchreibweg) — nie in ein fremdes Projekt.
    const neuerSchreibweg = (): Schreibweg =>
        schuetzeSchreibweg({
            projektId: projektIdVon(holeBericht()),
            istEingehaengt: istEingehaengt ?? (() => true),
            aktuell: holeBericht,
            onUpdate,
        });
    const kontextVon = (weg: Schreibweg): BuchungsKontext => ({
        report: weg.holeBericht(),
        onUpdate: weg.onUpdate,
        user,
        holeBericht: weg.holeBericht,
    });

    const neuLaden = () => setLadeZaehler((n) => n + 1);

    /** `stundenVorbelegt`: Startwert des Stundenfelds („Stunden ändern", Kontingent ändern). */
    const oeffneAktion = (a: Exclude<Aktion, null>, stundenVorbelegt = '') => {
        setAktion(a);
        setLeistungstag(heuteBerlin());
        setNotiz('');
        setGrund('');
        setStundenEingabe(stundenVorbelegt);
        setMeldung(null);
    };

    // Riegel gegen den Doppelklick: der State `laeuft` sperrt die Knoepfe erst
    // nach dem naechsten Render, zwei Klicks davor liefen sonst beide los —
    // und zwei Laeufe von nachHandErledigung saehen beide noch keine
    // BST-Zeile im Stand.
    const laeuftRef = useRef(false);
    const fuehreAus = async (arbeit: () => Promise<Ergebnis>) => {
        if (laeuftRef.current) return;
        laeuftRef.current = true;
        setLaeuft(true);
        setMeldung(null);
        try {
            const r = await arbeit();
            setMeldung(r.meldung);
            if (r.schliessen) setAktion(null);
        } catch (e) {
            setMeldung({ ton: 'fehler', text: describeError(e) });
        } finally {
            laeuftRef.current = false;
            setLaeuft(false);
            neuLaden();
        }
    };

    const gesamt = stand.gesamt;

    // ── Erledigt buchen / Uebernehmen ──────────────────────────────────────
    // `stunden` (S2): Pflicht, sobald die Datenbank sie kennt — das Formular
    // gibt den Knopf erst mit gueltigem Wert frei; undefined nur ohne Nr. 171.
    const erledigen = (b: PositionsBestandteil, ausgabe: AusgabeLike | null, stunden: number | undefined) =>
        fuehreAus(async () => {
            const weg = neuerSchreibweg();
            const rpc = await erledigeBestandteil({
                projectId,
                serviceId: service.id,
                key: b.key,
                // Uebernahme: Leistungstag leer → die Datenbank nimmt den Tag der Ausgabe.
                ...(ausgabe ? { ausgabeId: ausgabe.id } : { leistungstag, notiz }),
                ...(stunden !== undefined ? { stunden } : {}),
            });
            if (rpc.status !== 'erledigt') return { meldung: rpcMeldung(rpc), schliessen: rpc.status === 'schon_erledigt' };
            if (!weg.gueltig()) return { meldung: meldungProjektGewechselt(`„${b.label}“ erledigt.`), schliessen: true };
            const folge = await nachHandErledigung({
                ...kontextVon(weg),
                projectId,
                serviceId: service.id,
                bestandteilLabel: b.label,
                // Nur ein BEKANNTER Wert kuerzt ab; unbekannt (null) → nachHandErledigung liest frisch.
                ...(typeof rpc.offenDanach === 'number' ? { offenDanach: rpc.offenDanach } : {}),
            });
            return {
                meldung: meldungNachErledigung({ label: b.label, verb: 'erledigt', positionTitel: service.title, gesamt, offenDanach: rpc.offenDanach, folge }),
                schliessen: true,
            };
        });

    // ── Entfällt (nur Admin) ───────────────────────────────────────────────
    // Stunden freiwillig (S2), 0 erlaubt — undefined: ohne Stunden.
    const entfaellt = (b: PositionsBestandteil, stunden: number | undefined) =>
        fuehreAus(async () => {
            const weg = neuerSchreibweg();
            const rpc = await bestandteilEntfaellt({
                projectId,
                serviceId: service.id,
                key: b.key,
                grund: grund.trim(),
                ...(stunden !== undefined ? { stunden } : {}),
            });
            if (rpc.status !== 'entfaellt') return { meldung: rpcMeldung(rpc), schliessen: rpc.status === 'schon_erledigt' };
            if (rpc.offenDanach === 0 && !weg.gueltig()) return { meldung: meldungProjektGewechselt(`„${b.label}“ entfällt.`), schliessen: true };
            // Macht „Entfällt" die Liste vollstaendig, ist es die ausloesende
            // Handlung — die Buchung entscheidet nachHandErledigung (ohne
            // erledigten Bestandteil: 'nur_entfallen', dann der Knopf).
            const folge = rpc.offenDanach === 0
                ? await nachHandErledigung({ ...kontextVon(weg), projectId, serviceId: service.id, bestandteilLabel: b.label, offenDanach: 0 })
                : null;
            return {
                meldung: meldungNachErledigung({ label: b.label, verb: 'entfällt', positionTitel: service.title, gesamt, offenDanach: rpc.offenDanach, folge }),
                schliessen: true,
            };
        });

    // ── Zuruecknehmen ──────────────────────────────────────────────────────
    const zuruecknehmen = (b: PositionsBestandteil, eintragId: string) =>
        fuehreAus(async () => {
            const weg = neuerSchreibweg();
            const g = grund.trim();
            const rpc = await nimmBestandteilZurueck({ eintragId, grund: g });
            // position_fehlt, abrechnungsstand_unbekannt, veraltet, abgerechnet …:
            // Klartext aus RPC_STATUS_TEXT, an der Positionsbuchung geschieht nichts.
            if (rpc.status !== 'zurueckgenommen') {
                return { meldung: rpcMeldung(rpc), schliessen: rpc.status === 'schon_zurueckgenommen' };
            }
            if (!weg.gueltig()) return { meldung: meldungProjektGewechselt(`Eintrag zu „${b.label}“ zurückgenommen.`), schliessen: true };
            const folge = await nachBestandteilRuecknahme({
                ...kontextVon(weg),
                projectId,
                serviceId: service.id,
                grund: g,
                // Immer der Status der RPC (Paket C): nur 'zurueckgenommen' darf die Positionsbuchung anfassen.
                rpcStatus: rpc.status,
            });
            return { meldung: meldungNachRuecknahme({ label: b.label, positionTitel: service.title, folge }), schliessen: true };
        });

    // ── Stunden nachtragen / aendern (S7) ──────────────────────────────────
    // Rein intern: an der Positionsbuchung aendert sich nichts. Die neue Summe
    // bringt das Neuladen danach (Kopf und, ueber die Meldung, die Plakette).
    const stundenSetzen = (b: PositionsBestandteil, e: BestandteilEintrag, aendern: boolean) =>
        fuehreAus(async () => {
            const pruef = stundenSetzenEingabe({ eingabe: stundenEingabe, grund, art: e.art, aendern });
            if (!pruef.ok || pruef.stunden === null) {
                return { meldung: { ton: 'fehler', text: pruef.wertOk ? 'Bitte einen Grund mit mindestens 5 Zeichen angeben.' : STUNDEN_FORMAT_HINWEIS }, schliessen: false };
            }
            const g = grund.trim();
            const rpc = await setzeBestandteilStunden({ eintragId: e.id, stunden: pruef.stunden, ...(g ? { grund: g } : {}) });
            // Nachtragen, aber inzwischen stehen Stunden da: den Wert frisch
            // lesen, damit der Hinweis ihn nennt (das Neuladen danach zeigt ihn
            // in der Zeile). Ohne Ladung bleibt der Hinweis ohne Zahl.
            let stundenJetzt: number | null = typeof rpc.stunden === 'number' ? rpc.stunden : null;
            if (stundenJetzt === null && stundenInzwischenEingetragen(rpc.status, aendern)) {
                const ein = await ladeBestandteilEintraege(projectId);
                if (ein.ok) stundenJetzt = ein.eintraege.find((x) => gleicheId(x.id, e.id))?.stunden ?? null;
            }
            return meldungNachStunden(rpc, b.label, { aendern, stundenJetzt });
        });

    // ── Kontingent festlegen / aendern / entfernen (nur Admin, S1) ──────────
    // Der einzige Weg, ein bestehendes Kontingent zu aendern (S10). Nach
    // `gesetzt` die Position im Projektstand mit Kontingent UND Summe aus der
    // Antwort — EIN onUpdate ueber den geschuetzten Schreibweg.
    const speichereKontingent = (wert: number | null) =>
        fuehreAus(async () => {
            const weg = neuerSchreibweg();
            const g = grund.trim();
            const rpc = await setzeStundenKontingent({ projectId, serviceId: service.id, stunden: wert, ...(g ? { grund: g } : {}) });
            const meldungK = meldungNachKontingent(rpc, wert);
            if (rpc.status !== 'gesetzt') return { meldung: meldungK, schliessen: rpc.status === 'unveraendert' };
            const felder = kontingentFelderAusAntwort(rpc, wert);
            // Der Kopf bevorzugt den frischen DB-Wert — sofort den neuen, bis
            // das Neuladen ihn bestaetigt.
            setDbStunden({
                ...(dbStunden ?? {}),
                kontingent: felder.stundenKontingent ?? null,
                ...(felder.stundenErfasst !== undefined ? { erfasst: felder.stundenErfasst } : {}),
            });
            if (!weg.gueltig()) return { meldung: meldungProjektGewechselt(meldungK.text), schliessen: true };
            const neu = berichtMitStunden(weg.holeBericht(), service.id, felder);
            if (neu) weg.onUpdate(neu);
            return { meldung: meldungK, schliessen: true };
        });

    // ── Position jetzt buchen (bewusst) ────────────────────────────────────
    const jetztBuchen = () =>
        fuehreAus(async () => {
            // Unlesbare Liste: kein Buchen (der Knopf steht dann gar nicht da;
            // hier die zweite Reihe). Danach entscheidet allein der DB-Wert 0.
            if (unlesbar) return { meldung: { ton: 'hinweis', text: UNLESBAR_HINWEIS }, schliessen: true };
            const weg = neuerSchreibweg();
            const [pos, ein] = await Promise.all([ladePaketPositionenFrisch(projectId), ladeBestandteilEintraege(projectId)]);
            if (pos.ok === false) return { meldung: { ton: 'fehler', text: pos.fehler }, schliessen: false };
            const frisch = pos.positionen.find((p) => gleicheId(p.id, service.id));
            if (!frisch) {
                return { meldung: { ton: 'hinweis', text: 'Laut Datenbank hat die Position keine Bestandteile (mehr) — bitte neu laden.' }, schliessen: true };
            }
            if (frisch.bestandteileOffen !== 0) return { meldung: meldungNachJetztBuchen(service.title, { status: 'nicht_noetig', grund: 'nicht_vollstaendig' }), schliessen: true };
            if (ein.ok === false) return { meldung: { ton: 'fehler', text: ein.fehler }, schliessen: false };
            const leistung = leistungAusEintraegen({ id: frisch.id, bestandteile: frisch.bestandteile }, ein.eintraege);
            // Nichts erledigt, alles entfallen: Admin-Entscheidung (E4) — auch
            // hier, falls der Stand sich seit dem Oeffnen geaendert hat.
            if (leistung.leistungstage.length === 0 && !isAdmin) {
                return { meldung: { ton: 'hinweis', text: JETZT_BUCHEN_NUR_ADMIN }, schliessen: true };
            }
            if (!weg.gueltig()) return { meldung: meldungProjektGewechselt('Es wurde nichts gebucht.'), schliessen: true };
            const r = bucheVollstaendigePosition({
                ...kontextVon(weg),
                serviceId: frisch.id,
                frisch,
                leistungstage: leistung.leistungstage,
                bestandteilLabel: AUSLOESER_KNOPF,
                bewusst: true,
            });
            return { meldung: meldungNachJetztBuchen(service.title, r), schliessen: true };
        });

    // ── Liste festlegen (nur Admin) ────────────────────────────────────────
    const oeffneEditor = () => {
        setEntwurf(normalisiereBestandteile(service.bestandteile));
        setEditorFehler(null);
        setEditorOffen(true);
        setMeldung(null);
    };

    const speichereDefinition = async () => {
        if (laeuftRef.current) return;
        laeuftRef.current = true;
        setLaeuft(true);
        setEditorFehler(null);
        const weg = neuerSchreibweg();
        // Unlesbare Liste: neu speichern (auch unveraendert), aber nie mit Buchung.
        const reparatur = unlesbar;
        try {
            // Die Eintraege FRISCH — ob die neue Liste vollstaendig ist (und
            // damit gebucht wird), darf nicht am Stand vom Oeffnen haengen.
            const ein = await ladeBestandteilEintraege(projectId);
            if (ein.ok === false) {
                setEditorFehler(`${ein.fehler} — ohne die Einträge lässt sich nicht prüfen, ob die Position danach vollständig ist.`);
                return;
            }
            if (!weg.gueltig()) {
                setEditorFehler('Die Ansicht wurde verlassen oder das Projekt gewechselt — es wurde nichts gespeichert.');
                return;
            }
            const plan = planeDefinitionSpeichern({ bericht: weg.holeBericht(), serviceId: service.id, neu: entwurf, eintraege: ein.eintraege, reparatur });
            if (plan.ok === false) {
                setEditorFehler(plan.text);
                return;
            }
            if (plan.unveraendert) {
                setEditorOffen(false);
                setMeldung({ ton: 'hinweis', text: 'Keine Änderung — es wurde nichts gespeichert.' });
                return;
            }
            // EIN onUpdate: wird die Position dabei vollstaendig, bucht
            // bucheVollstaendigePosition auf dem neuen Stand und schreibt Liste und
            // Buchung zusammen. Zwei onUpdate hintereinander baute der zweite auf
            // dem alten Stand auf (die Ref im Hub folgt erst nach dem Render).
            let geschrieben = false;
            const schreibe = (r: Report) => {
                geschrieben = true;
                weg.onUpdate(r);
            };
            const folge = plan.buchen
                ? bucheVollstaendigePosition({ report: plan.bericht, holeBericht: () => plan.bericht, onUpdate: schreibe, user, serviceId: service.id, ...plan.buchen })
                : null;
            if (!geschrieben) weg.onUpdate(plan.bericht);
            // Der alte DB-Wert gehoert zur alten Liste — bis zum naechsten frischen
            // Wert gilt der Anzeige-Stand der Position im neuen Projektstand.
            setDbOffen(undefined);
            setEditorOffen(false);
            setMeldung(reparatur ? { ton: 'hinweis', text: REPARATUR_MELDUNG } : meldungNachDefinition(service.title, folge));
        } catch (e) {
            setEditorFehler(describeError(e));
        } finally {
            laeuftRef.current = false;
            setLaeuft(false);
            neuLaden();
        }
    };

    // ── Darstellung ────────────────────────────────────────────────────────
    const definition = stand.zeilen;
    // Paket ohne eine LESBARE Zeile (A3: `[]` + offene in der DB) — der Editor
    // startet mit leerer Liste (Reparatur: neu festlegen oder leeren).
    const paketOhneLesbare = definition.length === 0 && hatBestandteile(service);
    const kurzLautProjekt = bestandteilStandKurz(service);

    /** Ein Stundenfeld — Komma oder Punkt, auf dem Handy die Zifferntastatur. */
    const stundenFeld = (beschriftung: string, ungueltig: string | null, platzhalter = 'z. B. 7,5') => (
        <>
            <label className={feldLabel}>
                {beschriftung}
                <input
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    value={stundenEingabe}
                    onChange={(e) => setStundenEingabe(e.target.value)}
                    className={feld}
                    placeholder={platzhalter}
                    aria-invalid={ungueltig ? true : undefined}
                />
            </label>
            {ungueltig && <p className="text-[11px] font-semibold text-red-700">{ungueltig}</p>}
        </>
    );

    const renderFormular = (z: BestandteilZeile) => {
        if (!aktion || !('key' in aktion) || aktion.key !== z.bestandteil.key) return null;
        const b = z.bestandteil;
        if (aktion.art === 'erledigen') {
            // „Übernehmen" (S2): dasselbe Formular, die Ausgabe statt des
            // Leistungstags (die Datenbank nimmt den Tag der Ausgabe).
            const uebernahme = aktion.ausgabe ?? null;
            const tagOk = uebernahme ? true : leistungstagGueltig(leistungstag, heute);
            const st = erledigenStunden(stundenEingabe, stundenVerfuegbar);
            return (
                <div className="mt-3 space-y-3 rounded-lg bg-slate-50 p-3">
                    {uebernahme && (
                        <p className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
                            Übernahme: {uebernahmeInfo(b, uebernahme)}. Als Leistungstag gilt der Tag der Ausgabe.
                        </p>
                    )}
                    {!uebernahme && (
                        <>
                            <label className={feldLabel}>
                                Leistungstag
                                <input type="date" value={leistungstag} max={heute} onChange={(e) => setLeistungstag(e.target.value)} className={feld} />
                            </label>
                            {!tagOk && <p className="text-[11px] font-semibold text-red-700">Bitte einen Tag bis heute wählen — der Leistungstag darf nicht in der Zukunft liegen.</p>}
                        </>
                    )}
                    {stundenVerfuegbar
                        ? stundenFeld('Stunden (Pflicht)', stundenEingabe.trim() && !st.ok ? STUNDEN_FORMAT_HINWEIS : null)
                        : <p className="text-[11px] text-slate-500">{STUNDEN_NICHT_VERFUEGBAR}</p>}
                    {!uebernahme && (
                        <label className={feldLabel}>
                            Notiz (optional)
                            <textarea rows={2} value={notiz} onChange={(e) => setNotiz(e.target.value)} className={`${feld} resize-none`} placeholder="z. B. Termin mit dem Bauherrn am Vormittag" />
                        </label>
                    )}
                    <p className="text-[11px] text-slate-500">
                        Datum, Uhrzeit und Ihr Name werden beim Buchen automatisch festgehalten.
                        {stundenVerfuegbar ? ' Die Stunden zählen intern gegen das Stundenkontingent der Position.' : ''}
                    </p>
                    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                        <button type="button" onClick={() => setAktion(null)} className={knopfLeise} disabled={laeuft}>Abbrechen</button>
                        <button type="button" onClick={() => { void erledigen(b, uebernahme, st.stunden); }} className={knopfPrimaer} disabled={laeuft || !tagOk || !st.ok}>
                            {laeuft ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} {uebernahme ? 'Übernehmen' : 'Erledigt buchen'}
                        </button>
                    </div>
                </div>
            );
        }
        if (aktion.art === 'stunden') {
            const e = z.eintrag;
            // Der Eintrag ist inzwischen ein anderer (neu geladen, zurueckgenommen).
            if (!e || e.id !== aktion.eintragId) {
                return (
                    <div className="mt-3 flex flex-col gap-2 rounded-lg bg-slate-50 p-3 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-xs text-slate-600">Der Eintrag hat sich inzwischen geändert.</p>
                        <button type="button" onClick={() => setAktion(null)} className={knopfLeise}>Schließen</button>
                    </div>
                );
            }
            const pruef = stundenSetzenEingabe({ eingabe: stundenEingabe, grund, art: e.art, aendern: aktion.aendern });
            const formatFehler = stundenEingabe.trim() && !pruef.wertOk
                ? (e.art === 'entfaellt' ? STUNDEN_FORMAT_HINWEIS_ENTFAELLT : STUNDEN_FORMAT_HINWEIS)
                : null;
            return (
                <div className="mt-3 space-y-3 rounded-lg bg-slate-50 p-3">
                    {stundenFeld(aktion.aendern ? 'Stunden (neu)' : 'Stunden', formatFehler)}
                    {aktion.aendern && (
                        <label className={feldLabel}>
                            Grund der Änderung (mindestens 5 Zeichen)
                            <textarea rows={2} value={grund} onChange={(ev) => setGrund(ev.target.value)} className={`${feld} resize-none`} placeholder="z. B. Nacharbeit am Plan vergessen" />
                        </label>
                    )}
                    <p className="text-[11px] text-slate-500">
                        {aktion.aendern ? 'Die Änderung steht mit Grund im Verlauf.' : 'Die Stunden zählen intern gegen das Stundenkontingent der Position.'}
                    </p>
                    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                        <button type="button" onClick={() => setAktion(null)} className={knopfLeise} disabled={laeuft}>Abbrechen</button>
                        <button type="button" onClick={() => { void stundenSetzen(b, e, aktion.aendern); }} className={knopfPrimaer} disabled={laeuft || !pruef.ok}>
                            {laeuft ? <Loader2 className="h-4 w-4 animate-spin" /> : <Clock className="h-4 w-4" />} Stunden speichern
                        </button>
                    </div>
                </div>
            );
        }
        const istEntfaellt = aktion.art === 'entfaellt';
        const ok = grundGueltig(grund);
        // „Entfällt": Stunden freiwillig (S2), 0 erlaubt.
        const stEntf = entfaelltStunden(stundenEingabe, stundenVerfuegbar);
        return (
            <div className="mt-3 space-y-3 rounded-lg bg-slate-50 p-3">
                {!istEntfaellt && (
                    <p className="text-xs text-slate-700">
                        Der Bestandteil wird wieder offen. Ist die Position automatisch gebucht und noch nicht abgerechnet, wird ihre Buchung mit zurückgenommen.
                    </p>
                )}
                {istEntfaellt && (
                    <p className="text-xs text-slate-700">
                        „{b.label}“ zählt danach nicht mehr als offen. Der Grund steht im Nachweis.
                    </p>
                )}
                <label className={feldLabel}>
                    Grund (mindestens 5 Zeichen)
                    <textarea rows={2} value={grund} onChange={(e) => setGrund(e.target.value)} className={`${feld} resize-none`} placeholder={istEntfaellt ? 'z. B. vom Bauherrn nicht beauftragt' : 'z. B. versehentlich gebucht'} />
                </label>
                {istEntfaellt && stundenVerfuegbar && (
                    <>
                        {stundenFeld('Stunden (optional)', stundenEingabe.trim() && !stEntf.ok ? STUNDEN_FORMAT_HINWEIS_ENTFAELLT : null, 'leer = keine')}
                        <p className="text-[11px] text-slate-500">Fiel trotzdem Arbeit an, zählen die Stunden intern gegen das Stundenkontingent.</p>
                    </>
                )}
                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <button type="button" onClick={() => setAktion(null)} className={knopfLeise} disabled={laeuft}>Abbrechen</button>
                    <button
                        type="button"
                        onClick={() => {
                            if (aktion.art === 'zuruecknehmen') void zuruecknehmen(b, aktion.eintragId);
                            else void entfaellt(b, stEntf.stunden);
                        }}
                        className={`${knopf} ${istEntfaellt ? 'bg-amber-600 text-white hover:bg-amber-700' : 'bg-red-600 text-white hover:bg-red-700'}`}
                        disabled={laeuft || !ok || (istEntfaellt && !stEntf.ok)}
                    >
                        {laeuft ? <Loader2 className="h-4 w-4 animate-spin" /> : istEntfaellt ? <MinusCircle className="h-4 w-4" /> : <RotateCcw className="h-4 w-4" />}
                        {istEntfaellt ? 'Entfällt buchen' : 'Zurücknehmen'}
                    </button>
                </div>
            </div>
        );
    };

    const renderZeile = (z: BestandteilZeile) => {
        const b = z.bestandteil;
        const e = z.eintrag;
        const vorschlag = geladen && z.status === 'offen' && istModulQuelle(b.quelle) ? uebernahmeVorschlag(b, ausgaben) : null;
        const k = zeilenKnoepfe({ zeile: z, schreibbar: aktiv, isAdmin, userId: user.id, abgerechnet: status.abgerechnet, vorschlag });
        const zs = zeilenStunden({ zeile: z, stundenVerfuegbar, schreibbar: aktiv, isAdmin, userId: user.id });
        const formOffen = !!aktion && 'key' in aktion && aktion.key === b.key;
        const ab = effektivesAb(b);
        const Icon = !geladen ? Circle : z.status === 'erledigt' ? CheckCircle2 : z.status === 'entfaellt' ? MinusCircle : Circle;
        const iconFarbe = !geladen ? 'text-slate-300' : z.status === 'erledigt' ? 'text-emerald-600' : z.status === 'entfaellt' ? 'text-amber-600' : 'text-slate-400';
        const leistung = e && z.status === 'erledigt' ? leistungstagText(e) : null;
        const entf = e && z.status === 'entfaellt' ? entfaelltText(e) : null;
        return (
            <li key={b.key} className="rounded-xl border border-slate-200 bg-white p-3">
                <div className="flex items-start gap-3">
                    <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${iconFarbe}`} aria-hidden />
                    <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <span className="break-words text-sm font-semibold text-slate-800">{b.label}</span>
                            {geladen && (
                                <span className={`rounded border px-1.5 text-[11px] font-semibold ${STATUS_CHIP[z.status].klasse}`}>{STATUS_CHIP[z.status].text}</span>
                            )}
                            {zs.anzeige && (
                                <span className="inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-1.5 text-[11px] font-semibold tabular-nums text-slate-700" title="Erfasste Stunden (intern)">
                                    <Clock className="h-3 w-3" aria-hidden /> {zs.anzeige}
                                </span>
                            )}
                            {zs.fehlen && (
                                <span className="inline-flex items-center gap-1 rounded border border-amber-200 bg-amber-50 px-1.5 text-[11px] font-semibold text-amber-800">
                                    <Clock className="h-3 w-3" aria-hidden /> Stunden fehlen
                                </span>
                            )}
                        </div>
                        <div className="text-[11px] text-slate-500">
                            {BESTANDTEIL_QUELLE_LABEL[b.quelle] ?? b.quelle}
                            {ab ? ` · erledigt ab ${ANLASS_LABEL[ab] ?? ab}` : ''}
                            {onNavigateModule && istModulQuelle(b.quelle) && (
                                <button
                                    type="button"
                                    onClick={() => onNavigateModule(b.quelle)}
                                    // Waehrend eines Vorgangs nicht wegnavigieren — dessen
                                    // Buchung waere sonst verworfen (Nachtlauf).
                                    disabled={laeuft}
                                    className="ml-2 inline-flex items-center gap-1 font-semibold text-blue-700 hover:text-blue-900 disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                    Zum Modul <ExternalLink className="h-3 w-3" />
                                </button>
                            )}
                        </div>

                        {e && z.status === 'erledigt' && (
                            <div className="mt-1.5 space-y-0.5">
                                <p className="text-xs text-slate-700">{erledigtText(e)}</p>
                                {leistung && <p className="text-[11px] text-slate-600">{leistung}</p>}
                                <p className="text-[11px] text-slate-500">{herkunftText(e, ausgaben)}</p>
                                {e.notiz && <p className="break-words text-[11px] italic text-slate-600">„{e.notiz}“</p>}
                            </div>
                        )}
                        {entf && (
                            <div className="mt-1.5 space-y-0.5">
                                <p className="break-words text-xs text-slate-700">{entf.text}</p>
                                {entf.wer && <p className="text-[11px] text-slate-500">{entf.wer}</p>}
                            </div>
                        )}

                        {k.uebernehmen && vorschlag && !formOffen && (
                            <div className="mt-2 flex flex-col gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 sm:flex-row sm:items-center">
                                <p className="min-w-0 flex-1 text-xs text-blue-900">{uebernahmeText(b, vorschlag)}</p>
                                {/* S2: auch die Uebernahme verlangt Stunden — der Klick oeffnet das Formular. */}
                                <button type="button" onClick={() => oeffneAktion({ art: 'erledigen', key: b.key, ausgabe: vorschlag })} className={`${knopf} bg-blue-600 text-white hover:bg-blue-700`} disabled={laeuft}>
                                    Übernehmen
                                </button>
                            </div>
                        )}

                        {!formOffen && (k.erledigen || k.entfaellt || k.zuruecknehmen || zs.nachtragen || zs.aendern) && (
                            <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                                {k.erledigen && (
                                    <button type="button" onClick={() => oeffneAktion({ art: 'erledigen', key: b.key })} className={knopfPrimaer} disabled={laeuft}>
                                        <CheckCircle2 className="h-4 w-4" /> Erledigt buchen
                                    </button>
                                )}
                                {k.entfaellt && (
                                    <button type="button" onClick={() => oeffneAktion({ art: 'entfaellt', key: b.key })} className={knopfSekundaer} disabled={laeuft}>
                                        <MinusCircle className="h-4 w-4" /> Entfällt
                                    </button>
                                )}
                                {zs.nachtragen && e && (
                                    <button
                                        type="button"
                                        onClick={() => oeffneAktion({ art: 'stunden', key: b.key, eintragId: e.id, aendern: false })}
                                        className={zs.fehlen ? `${knopf} border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100` : knopfSekundaer}
                                        disabled={laeuft}
                                    >
                                        <Clock className="h-4 w-4" /> Stunden eintragen
                                    </button>
                                )}
                                {zs.aendern && e && (
                                    <button
                                        type="button"
                                        onClick={() => oeffneAktion({ art: 'stunden', key: b.key, eintragId: e.id, aendern: true }, stundenVorbelegung(e.stunden))}
                                        className={knopfSekundaer}
                                        disabled={laeuft}
                                    >
                                        <Clock className="h-4 w-4" /> Stunden ändern
                                    </button>
                                )}
                                {k.zuruecknehmen && e && (
                                    <button type="button" onClick={() => oeffneAktion({ art: 'zuruecknehmen', key: b.key, eintragId: e.id })} className={knopfSekundaer} disabled={laeuft}>
                                        <RotateCcw className="h-4 w-4" /> Zurücknehmen
                                    </button>
                                )}
                            </div>
                        )}
                        {k.zuruecknehmenSperre && <p className="mt-2 text-[11px] text-slate-500">{k.zuruecknehmenSperre}</p>}
                        {renderFormular(z)}
                    </div>
                </div>
            </li>
        );
    };

    // ── Kopf: Stunden gegen das Kontingent (S5), Admin legt es fest ─────────
    const renderStunden = () => {
        const formOffen = aktion?.art === 'kontingent';
        const neu = kontingentAusEingabe(stundenEingabe);
        const balken = kopfStunden.balken;
        return (
            <div className="mt-3 border-t border-slate-100 pt-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        <Clock className="h-3.5 w-3.5" aria-hidden /> Stunden (intern)
                    </span>
                    {darfKontingent && !formOffen && (
                        <button
                            type="button"
                            onClick={() => oeffneAktion({ art: 'kontingent' }, stundenVorbelegung(kontingent))}
                            className={`${knopfLeise} w-full sm:w-auto`}
                            disabled={laeuft}
                        >
                            <Pencil className="h-4 w-4" /> {kontingent === null ? 'Kontingent festlegen' : 'Kontingent ändern'}
                        </button>
                    )}
                </div>
                <p className={`mt-1 text-sm font-semibold tabular-nums ${STUNDEN_TEXT[kopfStunden.ton]}`}>{kopfStunden.text}</p>
                {balken !== null && (
                    <div
                        className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-200"
                        role="progressbar"
                        aria-label="Stunden gegen das Kontingent"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={Math.round(balken)}
                    >
                        <div className={`h-full rounded-full ${STUNDEN_BALKEN[kopfStunden.ton]}`} style={{ width: `${balken}%` }} />
                    </div>
                )}
                {kopfStunden.ohneStunden && (
                    <p className="mt-2 flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-900">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden /> {kopfStunden.ohneStunden}
                    </p>
                )}
                {darfKontingent && formOffen && (
                    <div className="mt-3 space-y-3 rounded-lg bg-slate-50 p-3">
                        {stundenFeld('Stundenkontingent (h)', stundenEingabe.trim() && neu === null ? KONTINGENT_FORMAT_HINWEIS : null, 'z. B. 100')}
                        <label className={feldLabel}>
                            Grund (optional)
                            <input type="text" value={grund} onChange={(e) => setGrund(e.target.value)} className={feld} placeholder="z. B. Nachkalkulation" />
                        </label>
                        <p className="text-[11px] text-slate-500">Intern — steht nie auf Rechnung, Leistungsnachweis oder Angebot. Die Änderung steht im Verlauf.</p>
                        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            <button type="button" onClick={() => setAktion(null)} className={knopfLeise} disabled={laeuft}>Abbrechen</button>
                            {kontingent !== null && (
                                <button type="button" onClick={() => { void speichereKontingent(null); }} className={knopfSekundaer} disabled={laeuft}>
                                    Kontingent entfernen
                                </button>
                            )}
                            <button type="button" onClick={() => { if (neu !== null) void speichereKontingent(neu); }} className={knopfPrimaer} disabled={laeuft || neu === null}>
                                {laeuft && <Loader2 className="h-4 w-4 animate-spin" />} Kontingent speichern
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const chip = POSITIONS_CHIP[status.art];
    // „Position jetzt buchen": bei nur entfallenen Bestandteilen Admin-Sache (E4).
    const freigabe = jetztBuchenFreigabe(status, stand, isAdmin);
    const warnungen = editorOffen ? editorWarnungen(service, log, unlesbar) : [];

    return (
        <div className="space-y-3">
            {meldung && (
                <div ref={meldungRef} role="status" className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${MELDUNG_KLASSE[meldung.ton]}`}>
                    <p className="min-w-0 flex-1 break-words">{meldung.text}</p>
                    {meldung.neuLaden && (
                        <button type="button" onClick={() => window.location.reload()} className="shrink-0 font-semibold underline underline-offset-2">
                            App neu laden
                        </button>
                    )}
                    <button type="button" onClick={() => setMeldung(null)} aria-label="Meldung schließen" className="-m-1 shrink-0 rounded p-1 opacity-70 hover:opacity-100">
                        <X className="h-4 w-4" />
                    </button>
                </div>
            )}

            {rechte.grund && <p className="rounded-lg bg-slate-100 px-3 py-2 text-xs text-slate-600">{rechte.grund}</p>}

            {/* Kopf: Stand und Status der Position */}
            <section className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4">
                {daten.art === 'laedt' && (
                    <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Bestandteile werden geladen …</p>
                )}
                {daten.art === 'fehler' && (
                    <div className="space-y-2">
                        <p className="flex items-start gap-2 text-sm text-red-800"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {daten.text}</p>
                        <p className="text-xs text-slate-600">
                            Stand laut Projekt: {kurzLautProjekt ? `${kurzLautProjekt} erledigt` : 'unbekannt'} — die Position gilt bis dahin als nicht abrechenbar.
                        </p>
                        <button type="button" onClick={neuLaden} className={knopfSekundaer}>Erneut laden</button>
                    </div>
                )}
                {geladen && (
                    <>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="text-base font-semibold text-slate-800">
                                {unlesbar && stand.gesamt === 0 ? 'Keine lesbaren Bestandteile' : standText(stand)}
                            </div>
                            {chip && <span className={`rounded border px-1.5 text-[11px] font-semibold ${chip.klasse}`}>{chip.text}</span>}
                        </div>
                        <p className="mt-1 text-xs text-slate-600">{status.text}</p>
                        {status.hinweis && (status.art === 'unlesbar' ? (
                            <p className="mt-2 flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
                                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {status.hinweis}
                            </p>
                        ) : (
                            <p className="mt-1 text-[11px] text-slate-500">{status.hinweis}</p>
                        ))}
                        {status.abgerechnet && <p className="mt-1 text-[11px] text-slate-500">Die Position ist abgerechnet.</p>}
                        {aktiv && freigabe.hinweis && <p className="mt-2 text-[11px] font-semibold text-slate-600">{freigabe.hinweis}</p>}
                        {aktiv && freigabe.knopf && (
                            aktion?.art === 'jetzt_buchen' ? (
                                <div className="mt-3 space-y-2 rounded-lg bg-slate-50 p-3">
                                    <p className="text-xs text-slate-700">
                                        Die Position wird jetzt mit der beauftragten Menge gebucht und ist damit abrechenbar. Im Protokoll stehen Sie als auslösende Person.
                                    </p>
                                    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                                        <button type="button" onClick={() => setAktion(null)} className={knopfLeise} disabled={laeuft}>Abbrechen</button>
                                        <button type="button" onClick={() => { void jetztBuchen(); }} className={knopfPrimaer} disabled={laeuft}>
                                            {laeuft ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Jetzt buchen
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <button type="button" onClick={() => oeffneAktion({ art: 'jetzt_buchen' })} className={`${knopfPrimaer} mt-3 w-full sm:w-auto`} disabled={laeuft}>
                                    Position jetzt buchen
                                </button>
                            )
                        )}
                        {daten.ausgabenFehler && <p className="mt-2 text-[11px] text-slate-500">Übernahme-Vorschläge nicht verfügbar: {daten.ausgabenFehler}</p>}
                        {stundenAn && renderStunden()}
                    </>
                )}
            </section>

            {/* Je Bestandteil eine Zeile */}
            {definition.length > 0 ? (
                <ol className="space-y-2">{definition.map(renderZeile)}</ol>
            ) : (
                <p className="rounded-xl border border-dashed border-slate-300 px-3 py-4 text-center text-xs text-slate-500">
                    {/* `[]` mit offenen in der DB (A3): Paket aus lauter unlesbaren Elementen. */}
                    {paketOhneLesbare ? 'Die gespeicherte Liste enthält keinen Bestandteil, den diese App-Version lesen kann.' : 'Diese Position hat keine Bestandteile.'}
                </p>
            )}

            {/* Verlauf, juengste zuerst */}
            {geladen && verlaufListe.length > 0 && (
                <section className="rounded-xl border border-slate-200 bg-white">
                    <button
                        type="button"
                        onClick={() => setVerlaufOffen((v) => !v)}
                        className="flex min-h-11 w-full items-center justify-between gap-2 px-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 sm:min-h-9"
                        aria-expanded={verlaufOffen}
                    >
                        <span className="flex items-center gap-1.5"><History className="h-4 w-4" /> Verlauf ({verlaufListe.length})</span>
                        {verlaufOffen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                    {verlaufOffen && (
                        <ol className="space-y-2 border-t border-slate-100 px-3 py-3">
                            {verlaufListe.map((v, i) => (
                                <li key={`${v.zeitpunkt}-${i}`} className="text-xs">
                                    <div className="text-[11px] tabular-nums text-slate-500">{formatZeitpunkt(v.zeitpunkt)} · {v.person}</div>
                                    <div className="break-words text-slate-700">{v.text}</div>
                                </li>
                            ))}
                        </ol>
                    )}
                </section>
            )}

            {/* Festlegen — nur Admin (E6) */}
            {isAdmin && rechte.schreibbar && (
                editorOffen ? (
                    <section className="space-y-3 rounded-xl border border-slate-200 bg-white p-3 sm:p-4">
                        {warnungen.map((w) => (
                            <p key={w} className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
                                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {w}
                            </p>
                        ))}
                        <BestandteileEditor
                            wert={entwurf}
                            onChange={(neu) => { setEntwurf(neu); setEditorFehler(null); }}
                            belegteKeys={belegteKeys(service, eintraege, aenderungen)}
                            keysMitEintrag={keysMitAktivemEintrag(service, eintraege)}
                            vorlage={vorlage}
                            service={service}
                        />
                        {editorFehler && <p className="text-xs font-semibold text-red-700">{editorFehler}</p>}
                        {!geladen && <p className="text-[11px] text-slate-500">Speichern geht erst, wenn die Einträge geladen sind.</p>}
                        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            <button type="button" onClick={() => { setEditorOffen(false); setEditorFehler(null); }} className={knopfLeise}>Abbrechen</button>
                            <button type="button" onClick={() => { void speichereDefinition(); }} className={knopfPrimaer} disabled={!geladen || laeuft}>
                                {laeuft && <Loader2 className="h-4 w-4 animate-spin" />} Bestandteile speichern
                            </button>
                        </div>
                    </section>
                ) : (
                    <button type="button" onClick={oeffneEditor} className={`${knopfSekundaer} w-full sm:w-auto`}>
                        <Pencil className="h-4 w-4" /> {definition.length > 0 || paketOhneLesbare ? 'Bestandteile bearbeiten' : 'Bestandteile festlegen'}
                    </button>
                )
            )}
        </div>
    );
};

/**
 * Das Modal um die Liste. Auf dem Handy volle Breite von unten, auf dem
 * Desktop mittig. Ohne Weichzeichner und ohne Preise.
 */
export const BestandteileDialog: React.FC<BestandteileListeProps & { onClose: () => void }> = ({ onClose, ...props }) => {
    const { service } = props;
    return (
        <div
            className="fixed inset-0 z-[210] flex items-end justify-center bg-black/60 animate-fade-in sm:items-center sm:p-4"
            role="dialog"
            aria-modal="true"
            aria-label={`Bestandteile: ${service.title}`}
        >
            <div className="flex max-h-[94vh] w-full flex-col overflow-hidden rounded-t-2xl bg-slate-50 shadow-2xl animate-scale-in sm:max-w-2xl sm:rounded-2xl">
                <div className="flex shrink-0 items-start justify-between gap-3 bg-slate-900 px-4 py-3 text-white sm:px-5 sm:py-4">
                    <div className="min-w-0">
                        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                            <ListChecks className="h-3.5 w-3.5" /> Bestandteile
                        </div>
                        <h3 className="mt-0.5 break-words text-base font-semibold leading-snug">{service.title}</h3>
                        {service.lvPosition && <p className="mt-0.5 font-mono text-xs text-slate-400">LV {service.lvPosition}</p>}
                    </div>
                    <button type="button" onClick={onClose} aria-label="Schließen" className="flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded hover:bg-white/10">
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto p-3 sm:p-5">
                    <BestandteileListe {...props} />
                </div>
            </div>
        </div>
    );
};
